import { openMetadataApiClient } from '@/shared/api/core/axios-instance';
import { getEntityEndpoint } from '@/shared/api/openmetadata';
import { TAG_FQN_PREFIXES } from '@/shared/lib/constants';
import type { JsonPatchOperation } from '@/shared/types/common';

export type TierTag = {
  id: string;
  name: string;
  fullyQualifiedName: string;
  description: string;
  version: number;
  updatedAt: number;
  updatedBy: string;
  href: string;
  deprecated: boolean;
  deleted: boolean;
  provider: string;
  disabled: boolean;
  mutuallyExclusive: boolean;
};

export type TierOption = {
  id: string;
  name: string;
  fullyQualifiedName: string;
  description: string;
};

export const fetchAvailableTiers = async (): Promise<TierOption[]> => {
  const response = await openMetadataApiClient.get<{ data: TierTag[] }>('/tags', {
    params: {
      parent: 'Tier',
      limit: 50,
    },
  });

  const tiers = response.data.data || [];

  return tiers.map((tier) => ({
    id: tier.id,
    name: tier.name,
    fullyQualifiedName: tier.fullyQualifiedName,
    description: tier.description,
  }));
};

export const generateTierPatchOperation = (
  currentTags: Array<{ tagFQN: string; source?: string }>,
  newTierFQN: string | null,
  availableTiers: TierOption[]
): JsonPatchOperation[] => {
  const operations: JsonPatchOperation[] = [];

  const currentTierIndex = currentTags.findIndex((tag) => tag.tagFQN.startsWith(TAG_FQN_PREFIXES.TIER));

  if (newTierFQN === null) {
    if (currentTierIndex !== -1) {
      operations.push({
        op: 'remove',
        path: `/tags/${currentTierIndex}`,
      });
    }
    return operations;
  }

  const tierInfo = availableTiers.find((tier) => tier.fullyQualifiedName === newTierFQN);

  if (!tierInfo) {
    throw new Error(`Tier not found: ${newTierFQN}`);
  }

  const newTierValue = {
    name: tierInfo.name,
    description: tierInfo.description,
    tagFQN: tierInfo.fullyQualifiedName,
    labelType: 'Manual' as const,
    state: 'Confirmed' as const,
  };

  if (currentTierIndex !== -1) {
    operations.push({
      op: 'replace',
      path: `/tags/${currentTierIndex}`,
      value: newTierValue,
    });
  } else {
    operations.push({
      op: 'add',
      path: `/tags/${currentTags.length}`,
      value: newTierValue,
    });
  }

  return operations;
};

export const updateAssetTier = async (
  entityType: string,
  entityId: string,
  operations: JsonPatchOperation[]
): Promise<void> => {
  try {
    const endpoint = getEntityEndpoint(entityType);
    await openMetadataApiClient.patch(`/${endpoint}/${encodeURIComponent(entityId)}`, operations, {
      headers: {
        'Content-Type': 'application/json-patch+json',
      },
    });
  } catch (error) {
    console.error('Failed to update asset tier:', error);
    throw error;
  }
};
